/** Email-mapping preview — a small informational dialog. Enter an address, Check,
 *  and see which OTA (and which domain rule) it maps to plus the effective
 *  settings once overrides apply. Purely informational; no persistence. */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { ConsoleApiError } from '@/api/client';
import * as otasApi from '@/api/otas';
import type { EmailPreviewResult } from '@/api/otas';
import { AppDialog } from '@/components/AppDialog';

const yesNo = (v: boolean | undefined): string => (v === undefined ? '—' : v ? 'Yes' : 'No');

export function EmailMappingPreviewDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const mutation = useMutation({
    mutationFn: (email: string) => otasApi.emailPreview(email),
  });

  const check = (email: string) => {
    if (email.trim()) mutation.mutate(email.trim());
  };

  return (
    <AppDialog
      open={open}
      onClose={() => {
        mutation.reset();
        onClose();
      }}
      size="sm"
      title="Email mapping preview"
    >
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          const input = (e.currentTarget.elements.namedItem('email') as HTMLInputElement | null)?.value ?? '';
          check(input);
        }}
        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}
      >
        <TextField
          name="email"
          label="Email address"
          size="small"
          fullWidth
          placeholder="guest@expedia.com"
          autoFocus
        />
        <Button
          type="submit"
          variant="contained"
          disabled={mutation.isPending}
          sx={{ mt: 0.25 }}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Check
        </Button>
      </Box>

      <Box sx={{ mt: 2 }}>
        {mutation.isError && (
          <Alert severity="error">
            {mutation.error instanceof ConsoleApiError ? mutation.error.message : 'Lookup failed.'}
          </Alert>
        )}
        {mutation.data && <PreviewResult result={mutation.data} />}
      </Box>
    </AppDialog>
  );
}

function PreviewResult({ result }: { result: EmailPreviewResult }) {
  if (!result.matched) {
    return <Alert severity="info">No OTA mapping applies to this address.</Alert>;
  }
  const domainRule = result.domain
    ? `${result.domain.domain ?? ''} — ${result.domain.match_type_display ?? ''}`
    : '—';
  const eff = result.effective;
  return (
    <Stack spacing={1.5}>
      <Row label="OTA" value={result.ota?.name ?? '—'} />
      <Row label="Matched rule" value={domainRule} />
      <Divider />
      <Typography variant="subtitle2" color="text.secondary">
        Effective settings
      </Typography>
      <Row label="Unique emails" value={yesNo(eff?.unique_emails)} />
      <Row label="Plus supported" value={yesNo(eff?.plus_supported)} />
      <Row label="Contact email" value={eff?.contact_email || '—'} />
      <Row label="Contact phone" value={eff?.contact_phone || '—'} />
    </Stack>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Box>
  );
}
